import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import { combineLatest, fromEvent, interval, merge } from "rxjs";
import { DOCUMENT } from "@angular/common";
import { debounceTime, map, startWith } from "rxjs/operators";

import type { Subscription } from "rxjs";
import type { OnDestroy } from "@angular/core";

const REDIRECT_AFTER = 5 * 60 * 1000;

@Injectable({
	providedIn: "root",
})
export class IdleService implements OnDestroy {
	private router = inject(Router);
	private document = inject(DOCUMENT);

	private redirectSubscription?: Subscription;
	private lastInteraction = merge(
		fromEvent(this.document, "mousemove"),
		fromEvent(this.document, "mousedown"),
		fromEvent(this.document, "keydown"),
	).pipe(
		debounceTime(300),
		map(() => new Date()),
		startWith(new Date()),
	);

	ngOnDestroy(): void {
		this.redirectSubscription?.unsubscribe();
	}

	public startRedirectOnIdle(redirectTarget: string[]): void {
		this.redirectSubscription?.unsubscribe();

		const wantedRouteAfterIdle = this.router.createUrlTree(redirectTarget);

		// In theory we could skip idle tracking if we are at the wanted route to reduce event listeners and interval checks
		// . But for the sake of simplicity we ignore this for now and deal with the small (really small) perfomance hit
		this.redirectSubscription = combineLatest([interval(10000), this.lastInteraction]).subscribe(
			([, lastInteraction]) => {
				const idleTime = new Date().getTime() - lastInteraction.getTime();
				if (idleTime < REDIRECT_AFTER) {
					return;
				}

				const isWantedRoute = this.router.isActive(wantedRouteAfterIdle, {
					fragment: "ignored",
					matrixParams: "ignored",
					paths: "exact",
					queryParams: "exact",
				});

				if (!isWantedRoute) {
					console.log("Navigate do to idle");
					this.router.navigate(redirectTarget);
				}
			},
		);
	}
}
